import type { DestinationDefinition } from '@customerio/cdp-action-destinations';
import { createCdpApiClient } from '@/services/api/cdp-api';
import { withCdpTransportErrors } from '@/services/cdp-api';
import type { AvailableDestination } from './entity';

/**
 * Hand-written client for the CDP backend's destination catalog. CDP has no
 * OpenAPI spec, so these endpoints are not in the generated `hydraUiApi`
 * surface; this follows `message-channel-setting/client.ts`. Failures arrive as
 * HydraUIAPIError, CdpNetworkError or an AbortError; read them with
 * `cdpRequestFailure` from `@/services/cdp-api`.
 */

type Json<T> = { content: { 'application/json': T } };
type OptionPath = { workspaceId: number; optionId: string };

interface CdpDestinationOptionPaths {
  '/workspaces/{workspaceId}/available_destinations': {
    get: {
      parameters: { path: { workspaceId: number } };
      responses: { 200: Json<{ destinations: AvailableDestination[] }> };
    };
  };
  '/workspaces/{workspaceId}/action_destinations/{optionId}/manifest': {
    get: { parameters: { path: OptionPath }; responses: { 200: Json<DestinationDefinition> } };
  };
  '/workspaces/{workspaceId}/action_destinations/{optionId}/validate': {
    post: {
      parameters: { path: OptionPath };
      requestBody: Json<{ settings: unknown }>;
      responses: { 200: Json<unknown> };
    };
  };
}

const client = createCdpApiClient<CdpDestinationOptionPaths>();

export const cdpDestinationOptionsApi = {
  list: async (workspaceId: number) => {
    const data = await withCdpTransportErrors(
      client.get('/workspaces/{workspaceId}/available_destinations', { params: { path: { workspaceId } } }),
    );
    return data.destinations;
  },

  manifest: (workspaceId: number, optionId: string) =>
    withCdpTransportErrors(
      client.get('/workspaces/{workspaceId}/action_destinations/{optionId}/manifest', {
        params: { path: { workspaceId, optionId } },
      }),
    ),

  /** Resolves when CDP accepts the settings; rejects with the reason when it doesn't. */
  validateSettings: async (workspaceId: number, optionId: string, settings: unknown) => {
    await withCdpTransportErrors(
      client.post('/workspaces/{workspaceId}/action_destinations/{optionId}/validate', {
        params: { path: { workspaceId, optionId } },
        body: { settings },
        // No timeout: CDP tests the customer's credentials against their storage, which can
        // outlast the 30 s default, and an abort would read as "check your credentials".
        timeout: 0,
      }),
    );
  },
};
